/**
 * Dashboard post-its section: personal / assigned boards, create/edit, archive.
 */

import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { PersonalPostItGrid } from '@/components/post-its/personal/PersonalPostItGrid';
import { PostItModal } from '@/components/post-its/PostItModal';
import { ShareModal } from '@/components/post-its/ShareModal';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { SegmentedControl, type SegmentOption } from '@/components/forms/SegmentedControl';
import type {
  CreateAssignedPostItDto,
  AssignedPostIt,
  PostIt,
  CreatePostItDto,
  UpdatePostItDto,
  PostItStatus,
} from '@/types/post-it.types';
import { PostItsPlanningBoard } from '@/components/post-its/planning/PostItsPlanningBoard';
import { PostItsPlanningFilters } from '@/components/post-its/planning/PostItsPlanningFilters';
import { PersonalPostItsFilters } from '@/components/post-its/personal/PersonalPostItsFilters';
import { type AssignedFilter, type DeadlineFilter } from '@/components/post-its/post-it-configs';
import { PostItsPlanningSelectionBar } from '@/components/post-its/planning/PostItsPlanningSelectionBar';
import { type SelectionActionConfig } from '@/components/post-its/post-it-form-utils';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import type { CalendarSyncFilterSeed } from '@/utils/calendar-sync-utils';
import { Plus, Filter, BadgePlus, RefreshCw, StickyNote, User, Users } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPostIts,
  createPostIt,
  updatePostIt,
  deletePostIt,
  togglePriority,
  updateStatus,
  sharePostIt,
  sharePostItByRole,
  removeShare,
  getShares,
  hardDeletePostIt,
  emptyTrash,
  toggleArchive,
  createAssignedPostIt,
  updateAssignedPostIt,
  deleteAssignedPostIt,
  bulkUpdateAssignedPostIts,
  bulkDeleteAssignedTemplates,
  bulkUpdateAssignedTemplates,
} from '@/services/post-its.service';
import type { ElementWidth } from '@/types/interface-elements.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';

/** Personal notes vs notes assigned to the current user. */
type PostItBoardView = 'personal' | 'assigned';

/** Map dashboard deadline chips to the calendar-sync post-it bag. */
function postItCalendarFilterSeed(
  filter: DeadlineFilter,
  from: string,
  to: string,
): CalendarSyncFilterSeed {
  if (filter === 'week' || filter === 'month' || filter === 'year' || filter === 'all') {
    return { postitFilters: { dateRange: filter } };
  }
  return {
    postitFilters: {
      dateFrom: from || undefined,
      dateTo: to || undefined,
    },
  };
}

/** Segmented-control options for the post-it board view. */
const BOARD_VIEW_OPTIONS: readonly SegmentOption<PostItBoardView>[] = [
  { value: 'personal', label: 'Mes post-its', icon: <User className="w-4 h-4" /> },
  { value: 'assigned', label: 'Post-its assignés', icon: <Users className="w-4 h-4" /> },
];

/** Props for {@link PostItsSection}. */
interface PostItsSectionProps {
  sectionWidth?: ElementWidth;
  onSuccess?: (action: 'create' | 'update' | 'delete') => void;
  onError?: (error: Error) => void;
}

/** Create when empty; read-only shares open in view; otherwise edit. */
function personalPostItModalMode(postIt: PostIt | null): 'create' | 'view' | 'edit' {
  if (!postIt) return 'create';
  if (postIt.userPermission === 'read') return 'view';
  return 'edit';
}

/** Props for {@link PostItsBoardContent}. */
interface PostItsBoardContentProps {
  showPersonalBoard: boolean;
  isPostItsError: boolean;
  onRetry: () => void;
  postIts: PostIt[];
  sectionWidth?: ElementWidth;
  isLoading: boolean;
  onEdit: (postIt: PostIt) => void;
  onDelete: (id: string, hardDelete?: boolean) => void;
  onTogglePriority: (id: string) => void;
  onToggleStatus: (id: string) => void;
  onShare: (postIt: PostIt) => void;
  onRestore: (id: string) => void;
  onEmptyTrash: () => void;
  loadingStates: Record<
    string,
    { deleting?: boolean; togglingPriority?: boolean; togglingStatus?: boolean; sharing?: boolean }
  >;
  assignedFilter: AssignedFilter;
  assignedEndDateFrom: string;
  assignedEndDateTo: string;
  assignedQuarterlyPeriods: number[];
  selectedIds: string[];
  onToggleSelect?: (id: string) => void;
  onDataReady: (ids: string[]) => void;
  onEditAssigned: (t: AssignedPostIt) => void;
  onDeleteTemplate: (id: string) => void;
}

/** Personal grid or assigned board, including the personal-board error retry. */
function PostItsBoardContent({
  showPersonalBoard,
  isPostItsError,
  onRetry,
  postIts,
  sectionWidth,
  isLoading,
  onEdit,
  onDelete,
  onTogglePriority,
  onToggleStatus,
  onShare,
  onRestore,
  onEmptyTrash,
  loadingStates,
  assignedFilter,
  assignedEndDateFrom,
  assignedEndDateTo,
  assignedQuarterlyPeriods,
  selectedIds,
  onToggleSelect,
  onDataReady,
  onEditAssigned,
  onDeleteTemplate,
}: Readonly<PostItsBoardContentProps>) {
  if (showPersonalBoard) {
    if (isPostItsError) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-500">{getErrorMessage('loadPostItsFailed')}</p>
            <Button variant="outline" className="mt-4" onClick={onRetry}>
              Réessayer
            </Button>
          </div>
        </div>
      );
    }
    return (
      <PersonalPostItGrid
        postIts={postIts}
        sectionWidth={sectionWidth}
        isLoading={isLoading}
        onReorder={() => {}}
        onEdit={onEdit}
        onDelete={onDelete}
        onTogglePriority={onTogglePriority}
        onToggleStatus={onToggleStatus}
        onShare={onShare}
        onRestore={onRestore}
        onEmptyTrash={onEmptyTrash}
        loadingStates={loadingStates}
      />
    );
  }

  return (
    <PostItsPlanningBoard
      filter={assignedFilter}
      endDateFrom={assignedEndDateFrom || undefined}
      endDateTo={assignedEndDateTo || undefined}
      quarterlyPeriods={assignedQuarterlyPeriods}
      selectedIds={selectedIds}
      onToggleSelect={onToggleSelect}
      onDataReady={onDataReady}
      onEditAssigned={onEditAssigned}
      onDeleteTemplate={onDeleteTemplate}
      sectionWidth={sectionWidth}
    />
  );
}

/** Dashboard widget: personal post-it notes. */
export const PostItsSection: React.FC<PostItsSectionProps> = ({
  sectionWidth = 'full',
  onSuccess,
  onError,
}) => {
  const queryClient = useQueryClient();

  const { elements: dashboardElements } = useInterfaceElements('dashboard');
  /** Dashboard IE: personal/assigned toggle. */
  const canToggleBoard = dashboardElements.some((el) => el.elementId === 'postits-board-toggle');
  /** Dashboard IE: assigned-board date/quarter filters. */
  const canFilterAssigned = dashboardElements.some(
    (el) => el.elementId === 'postits-assigned-filter',
  );
  /** Dashboard IE: multi-select + bulk actions on assigned templates. */
  const canSelectAssigned = dashboardElements.some(
    (el) => el.elementId === 'postits-assigned-selection-bar',
  );
  /** Dashboard IE: create assigned template. */
  const canCreateAssigned = dashboardElements.some(
    (el) => el.elementId === 'postits-assigned-create',
  );

  const [syncOpen, setSyncOpen] = React.useState(false);
  const [boardView, setBoardView] = React.useState<PostItBoardView>('personal');
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [assignedFilter, setAssignedFilter] = React.useState<AssignedFilter>('all');
  const [assignedEndDateFrom, setAssignedEndDateFrom] = React.useState('');
  const [assignedEndDateTo, setAssignedEndDateTo] = React.useState('');
  const [assignedQuarterlyPeriods, setAssignedQuarterlyPeriods] = React.useState<number[]>([]);
  const [showAssignedFilters, setShowAssignedFilters] = React.useState(false);
  const [showPersonalFilters, setShowPersonalFilters] = React.useState(false);
  const [showQuickBar, setShowQuickBar] = React.useState(false);
  const [assignedModalOpen, setAssignedModalOpen] = React.useState(false);
  const [editingAssignedPostIt, setEditingAssignedPostIt] = React.useState<AssignedPostIt | null>(
    null,
  );
  const [assignedViewModalOpen, setAssignedViewModalOpen] = React.useState(false);

  const [postItModalOpen, setPostItModalOpen] = React.useState(false);
  const [editingPostIt, setEditingPostIt] = React.useState<PostIt | null>(null);
  const [shareModalOpen, setShareModalOpen] = React.useState(false);
  const [postItToShare, setPostItToShare] = React.useState<PostIt | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [postItToDelete, setPostItToDelete] = React.useState<string | null>(null);
  const [isHardDelete, setIsHardDelete] = React.useState(false);
  const [emptyTrashModalOpen, setEmptyTrashModalOpen] = React.useState(false);
  const [deadlineFrom, setDeadlineFrom] = React.useState('');
  const [deadlineTo, setDeadlineTo] = React.useState('');
  const [deadlineFilter, setDeadlineFilter] = React.useState<DeadlineFilter>('all');
  const [quarterlyPeriods, setQuarterlyPeriods] = React.useState<number[]>([]);
  const [loadingStates, setLoadingStates] = React.useState<
    Record<
      string,
      {
        deleting?: boolean;
        togglingPriority?: boolean;
        togglingStatus?: boolean;
        sharing?: boolean;
      }
    >
  >({});

  const {
    data: postItsResponse,
    isLoading,
    isError: isPostItsError,
    refetch: refetchPostIts,
  } = useQuery({
    queryKey: ['postits', deadlineFrom, deadlineTo],
    queryFn: () =>
      getPostIts({
        dateFrom: deadlineFrom || undefined,
        dateTo: deadlineTo || undefined,
      }),
  });

  const postIts = postItsResponse?.postIts || [];

  const createPostItMutation = useMutation({
    mutationFn: createPostIt,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      setPostItModalOpen(false);
      setEditingPostIt(null);
      toast.success('Post-it créé avec succès !');
      onSuccess?.('create');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error));
      onError?.(error);
    },
  });

  const updatePostItMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePostItDto }) => updatePostIt(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      setPostItModalOpen(false);
      setEditingPostIt(null);
      toast.success('Post-it modifié avec succès !');
      onSuccess?.('update');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error));
      onError?.(error);
    },
  });

  const archivePostItMutation = useMutation({
    mutationFn: deletePostIt,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      setDeleteModalOpen(false);
      setPostItToDelete(null);
      toast.success('Post-it archivé avec succès !');
      onSuccess?.('delete');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error));
      onError?.(error);
    },
  });

  const hardDeletePostItMutation = useMutation({
    mutationFn: hardDeletePostIt,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      setDeleteModalOpen(false);
      setPostItToDelete(null);
      toast.success('Post-it supprimé définitivement !');
      onSuccess?.('delete');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error));
      onError?.(error);
    },
  });

  const emptyTrashMutation = useMutation({
    mutationFn: emptyTrash,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      toast.success(`${data.count} post-it(s) supprimé(s) de la corbeille`);
      onSuccess?.('delete');
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error));
      onError?.(error);
    },
  });

  const togglePriorityMutation = useMutation({
    mutationFn: (id: string) => {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], togglingPriority: true } }));
      return togglePriority(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['postits'] });
      const previousData = queryClient.getQueryData(['postits']);

      queryClient.setQueryData(['postits'], (old: any) => {
        if (!old?.postIts) return old;
        return {
          ...old,
          postIts: old.postIts.map((p: PostIt) =>
            p.id === id ? { ...p, isPriority: !p.isPriority } : p,
          ),
        };
      });

      return { previousData };
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      toast.success('Priorité modifiée !');
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], togglingPriority: false } }));
    },
    onError: (error: Error, id, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['postits'], context.previousData);
      }
      toast.error(getErrorMessage(error));
      onError?.(error);
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], togglingPriority: false } }));
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PostItStatus }) => {
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], togglingStatus: true } }));
      return updateStatus(id, status);
    },
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['postits'] });
      const previousData = queryClient.getQueryData(['postits']);

      queryClient.setQueryData(['postits'], (old: any) => {
        if (!old?.postIts) return old;
        return {
          ...old,
          postIts: old.postIts.map((p: PostIt) => (p.id === id ? { ...p, status } : p)),
        };
      });

      return { previousData };
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      toast.success('Statut modifié !');
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], togglingStatus: false } }));
    },
    onError: (error: Error, { id }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['postits'], context.previousData);
      }
      toast.error(getErrorMessage(error));
      onError?.(error);
      setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], togglingStatus: false } }));
    },
  });

  /** Assigned board opens the assigned modal; personal board opens create. */
  const handleCreatePostIt = () => {
    if (boardView === 'assigned') {
      setAssignedModalOpen(true);
    } else {
      setEditingPostIt(null);
      setPostItModalOpen(true);
    }
  };

  const [assignedBoardIds, setAssignedBoardIds] = React.useState<string[]>([]);

  /** Toggle one assigned-template id in the bulk-selection set. */
  const handleToggleSelect = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSelectAll = () => setSelectedIds(assignedBoardIds);

  const handleClearSelection = () => setSelectedIds([]);

  const createAssignedMutation = useMutation({
    mutationFn: createAssignedPostIt,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['post-its', 'assigned'] });
      toast.success('Post-it assigné créé avec succès !');
      setAssignedModalOpen(false);
      onSuccess?.('create');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const bulkAssignedMutation = useMutation({
    mutationFn: bulkUpdateAssignedPostIts,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['post-its', 'assigned'] });
      toast.success(
        `${result.updated} post-it${result.updated === 1 ? '' : 's'} mis à jour avec succès`,
      );
      setSelectedIds([]);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const updateAssignedMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAssignedPostItDto> }) =>
      updateAssignedPostIt(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['post-its', 'assigned'] });
      toast.success('Post-it assigné mis à jour avec succès !');
      setAssignedViewModalOpen(false);
      setEditingAssignedPostIt(null);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const deleteAssignedTemplateMutation = useMutation({
    mutationFn: deleteAssignedPostIt,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['post-its', 'assigned'] });
      toast.success('Template supprimé avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const bulkDeleteAssignedTemplatesMutation = useMutation({
    mutationFn: bulkDeleteAssignedTemplates,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['post-its', 'assigned'] });
      toast.success(
        `${result.deleted} template${result.deleted === 1 ? '' : 's'} supprimé${result.deleted === 1 ? '' : 's'}`,
      );
      setSelectedIds([]);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const bulkPriorityAssignedTemplatesMutation = useMutation({
    mutationFn: bulkUpdateAssignedTemplates,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['post-its', 'assigned'] });
      toast.success('Templates marqués comme prioritaires');
      setSelectedIds([]);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Create an assigned template from the modal DTO. */
  const handleSaveAssignedPostIt = async (data: CreateAssignedPostItDto) => {
    await createAssignedMutation.mutateAsync(data);
  };

  /** Patch an assigned template from the view/edit modal. */
  const handleUpdateAssignedPostIt = async (id: string, data: Partial<CreateAssignedPostItDto>) => {
    await updateAssignedMutation.mutateAsync({ id, data });
  };

  /** Open the assigned template in the view/edit modal. */
  const handleEditAssigned = (t: AssignedPostIt) => {
    setEditingAssignedPostIt(t);
    setAssignedViewModalOpen(true);
  };

  const handleDeleteAssignedTemplate = (id: string) => {
    deleteAssignedTemplateMutation.mutate(id);
  };

  /** Hard-delete the selected assigned templates. */
  const handleBulkDeleteAssigned = () => {
    bulkDeleteAssignedTemplatesMutation.mutate(selectedIds);
  };

  /** Mark selected assigned templates as priority. */
  const handleBulkPriorityAssigned = () => {
    bulkPriorityAssignedTemplatesMutation.mutate({
      ids: selectedIds,
      data: { isPriority: true },
    });
  };

  /** Apply role / recurrence / dates from the assigned selection bar. */
  const handleApplySelection = (config: SelectionActionConfig) => {
    bulkAssignedMutation.mutate({
      ids: selectedIds,
      roleId: config.roleId,
      recurrence: config.recurrence,
      specificDateFrom: config.specificDateFrom,
      specificDateTo: config.specificDateTo,
      quarterlyPeriods: config.quarterlyPeriods,
      scheduleConfig: config.scheduleConfig,
    });
  };

  /** Create or update a personal post-it, then apply a pending share if any. */
  const handleSavePostIt = async (
    data: CreatePostItDto | UpdatePostItDto,
    pendingShare?: { userIds: string[]; permission: 'read' | 'write' },
  ) => {
    if (editingPostIt) {
      await updatePostItMutation.mutateAsync({
        id: editingPostIt.id,
        data,
      });
      if (pendingShare?.userIds.length) {
        try {
          await sharePostIt(
            editingPostIt.id,
            [...new Set(pendingShare.userIds)],
            pendingShare.permission,
          );
          void queryClient.invalidateQueries({ queryKey: ['postits'] });
        } catch (error) {
          toast.error(getErrorMessage(error));
          onError?.(error as Error);
        }
      }
    } else {
      const created = await createPostItMutation.mutateAsync(data as CreatePostItDto);
      if (pendingShare?.userIds.length && created?.id) {
        try {
          await sharePostIt(
            created.id,
            [...new Set(pendingShare.userIds)],
            pendingShare.permission,
          );
          void queryClient.invalidateQueries({ queryKey: ['postits'] });
        } catch (error) {
          toast.error(getErrorMessage(error));
          onError?.(error as Error);
        }
      }
    }
  };

  const handleEditPostIt = (postIt: PostIt) => {
    setEditingPostIt(postIt);
    setPostItModalOpen(true);
  };

  /** Open the confirm modal; `hardDelete` empties from trash instead of archive. */
  const handleDeletePostIt = (id: string, hardDelete = false) => {
    setPostItToDelete(id);
    setIsHardDelete(hardDelete);
    setDeleteModalOpen(true);
  };

  /** Archive or hard-delete the post-it selected in the confirm modal. */
  const confirmDeletePostIt = async () => {
    if (postItToDelete) {
      if (isHardDelete) {
        await hardDeletePostItMutation.mutateAsync(postItToDelete);
      } else {
        await archivePostItMutation.mutateAsync(postItToDelete);
      }
    }
  };

  /** Un-archive a personal post-it from the trash column. */
  const handleRestorePostIt = async (id: string) => {
    setLoadingStates((prev) => ({ ...prev, [id]: { ...prev[id], togglingStatus: true } }));
    try {
      await toggleArchive(id);
      await queryClient.invalidateQueries({ queryKey: ['postits'] });
      toast.success('Post-it restauré avec succès !');
      onSuccess?.('update');
    } catch (error) {
      toast.error(getErrorMessage(error));
      onError?.(error as Error);
    } finally {
      setLoadingStates((prev) => {
        const newState = { ...prev };
        delete newState[id]?.togglingStatus;
        return newState;
      });
    }
  };

  /** Open the empty-trash confirm modal (does not delete yet). */
  const handleEmptyTrash = () => {
    setEmptyTrashModalOpen(true);
  };

  /** Permanently delete every personal post-it in trash. */
  const confirmEmptyTrash = async () => {
    await emptyTrashMutation.mutateAsync();
    setEmptyTrashModalOpen(false);
  };

  const handleTogglePostItPriority = async (id: string) => {
    await togglePriorityMutation.mutateAsync(id);
  };

  /** Flip personal status between `done` and `en_cours`. */
  const handleTogglePostItStatus = async (id: string) => {
    const postIt = postIts.find((p) => p.id === id);
    if (!postIt) return;

    const newStatus = postIt.status === 'done' ? 'en_cours' : 'done';
    await toggleStatusMutation.mutateAsync({ id, status: newStatus });
  };

  const handleSharePostIt = (postIt: PostIt) => {
    setPostItToShare(postIt);
    setShareModalOpen(true);
  };

  const { data: cardSharesResponse } = useQuery({
    queryKey: ['postit-shares', postItToShare?.id],
    queryFn: () => getShares(postItToShare!.id),
    enabled: shareModalOpen && Boolean(postItToShare?.id),
  });

  /** Shares for the open {@link ShareModal} (`shares` wrapper or a raw array). */
  const cardExistingShares = React.useMemo(() => {
    if (cardSharesResponse?.shares && Array.isArray(cardSharesResponse.shares)) {
      return cardSharesResponse.shares;
    }
    if (Array.isArray(cardSharesResponse)) return cardSharesResponse;
    return postItToShare?.shares ?? [];
  }, [cardSharesResponse, postItToShare?.shares]);

  /** Share the open personal post-it with selected users. */
  const handleShareUsers = async (userIds: string[], permission: 'read' | 'write') => {
    if (!postItToShare) return;
    try {
      await sharePostIt(postItToShare.id, userIds, permission);
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      void queryClient.invalidateQueries({ queryKey: ['postit-shares', postItToShare.id] });
    } catch (error) {
      onError?.(error as Error);
    }
  };

  /** Share the open personal post-it with every user of a role. */
  const handleShareRole = async (roleCode: string, permission: 'read' | 'write') => {
    if (!postItToShare) return;
    try {
      await sharePostItByRole(postItToShare.id, roleCode, permission);
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
    } catch (error) {
      onError?.(error as Error);
    }
  };

  /** Revoke one user share on the open personal post-it. */
  const handleRemoveShare = async (userId: string) => {
    if (!postItToShare) return;
    try {
      await removeShare(postItToShare.id, userId);
      void queryClient.invalidateQueries({ queryKey: ['postits'] });
      void queryClient.invalidateQueries({ queryKey: ['postit-shares', postItToShare.id] });
    } catch (error) {
      onError?.(error as Error);
    }
  };

  function handleBoardViewChange(view: PostItBoardView): void {
    setBoardView(view);
    setSelectedIds([]);
    if (view === 'personal') {
      setShowAssignedFilters(false);
      setShowQuickBar(false);
      setAssignedFilter('all');
      setAssignedEndDateFrom('');
      setAssignedEndDateTo('');
      setAssignedQuarterlyPeriods([]);
    } else {
      setShowPersonalFilters(false);
      setDeadlineFilter('all');
      setQuarterlyPeriods([]);
      setDeadlineFrom('');
      setDeadlineTo('');
    }
  }

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }

  if (isLoading) {
    return (
      <div className="space-y-5">
        <div className="section-header">
          <div className="flex items-center gap-2 text-[var(--color-1-nuance-1)]">
            <StickyNote className="h-5 w-5" />
            <h2 className="section-title">Post-its</h2>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" label="Chargement..." />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <div className="section-header flex-shrink-0 mb-5">
          <div className="flex items-center gap-2 text-[var(--color-1-nuance-1)]">
            <StickyNote className="h-5 w-5" />
            <h2 className="section-title">
              {boardView === 'assigned' && canToggleBoard
                ? 'Planification post-its'
                : 'Mes post-its'}
            </h2>
          </div>
          <div className="section-actions">
            {CALENDAR_SYNC_ENABLED ? (
              <Button
                preset="section-action"
                icon={RefreshCw}
                tooltipText="Synchroniser"
                aria-label="Synchroniser"
                onClick={handleOpenSync}
              />
            ) : null}
            {canSelectAssigned && boardView === 'assigned' && (
              <Button
                preset="section-action"
                icon={BadgePlus}
                tooltipText="Sélection groupée"
                onClick={() => {
                  setShowQuickBar((prev) => {
                    if (prev) setSelectedIds([]);
                    return !prev;
                  });
                  setShowAssignedFilters(false);
                }}
                className={showQuickBar ? 'section-action-button-active' : undefined}
              />
            )}
            {canFilterAssigned && boardView === 'assigned' && (
              <Button
                preset="section-action"
                icon={Filter}
                tooltipText="Filtrer"
                onClick={() => {
                  setShowAssignedFilters((prev) => !prev);
                  setShowQuickBar(false);
                }}
                className={showAssignedFilters ? 'section-action-button-active' : undefined}
              />
            )}
            {boardView === 'personal' && (
              <Button
                preset="section-action"
                icon={Filter}
                tooltipText="Filtrer"
                onClick={() => setShowPersonalFilters((prev) => !prev)}
                className={showPersonalFilters ? 'section-action-button-active' : undefined}
              />
            )}
            <Button
              preset="section-action"
              icon={Plus}
              tooltipText={boardView === 'assigned' ? 'Nouveau post-it assigné' : 'Nouveau post-it'}
              onClick={handleCreatePostIt}
            />

            {canToggleBoard && (
              <SegmentedControl<PostItBoardView>
                preset="view-toggle"
                value={boardView}
                onChange={handleBoardViewChange}
                options={BOARD_VIEW_OPTIONS}
              />
            )}
          </div>
        </div>

        {boardView === 'personal' && showPersonalFilters && (
          <PersonalPostItsFilters
            filter={deadlineFilter}
            onFilterChange={setDeadlineFilter}
            deadlineFrom={deadlineFrom}
            deadlineTo={deadlineTo}
            onDeadlineFromChange={setDeadlineFrom}
            onDeadlineToChange={setDeadlineTo}
            quarterlyPeriods={quarterlyPeriods}
            onQuarterlyPeriodsChange={setQuarterlyPeriods}
          />
        )}

        {canFilterAssigned && boardView === 'assigned' && showAssignedFilters && (
          <PostItsPlanningFilters
            filter={assignedFilter}
            onFilterChange={(f) => {
              setAssignedFilter(f);
              if (f !== 'quarterly') {
                setAssignedQuarterlyPeriods([]);
              }
              setAssignedEndDateFrom('');
              setAssignedEndDateTo('');
            }}
            endDateFrom={assignedEndDateFrom}
            endDateTo={assignedEndDateTo}
            onEndDateFromChange={setAssignedEndDateFrom}
            onEndDateToChange={setAssignedEndDateTo}
            quarterlyPeriods={assignedQuarterlyPeriods}
            onQuarterlyPeriodsChange={setAssignedQuarterlyPeriods}
          />
        )}

        {canSelectAssigned && boardView === 'assigned' && showQuickBar && (
          <PostItsPlanningSelectionBar
            selectedIds={selectedIds}
            totalCount={assignedBoardIds.length}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onApply={handleApplySelection}
            onDelete={handleBulkDeleteAssigned}
            onSetPriority={handleBulkPriorityAssigned}
            isLoading={
              bulkAssignedMutation.isPending ||
              bulkDeleteAssignedTemplatesMutation.isPending ||
              bulkPriorityAssignedTemplatesMutation.isPending
            }
          />
        )}

        <div>
          <PostItsBoardContent
            showPersonalBoard={boardView === 'personal' || !canToggleBoard}
            isPostItsError={isPostItsError}
            onRetry={() => void refetchPostIts()}
            postIts={postIts}
            sectionWidth={sectionWidth}
            isLoading={isLoading}
            onEdit={handleEditPostIt}
            onDelete={handleDeletePostIt}
            onTogglePriority={handleTogglePostItPriority}
            onToggleStatus={handleTogglePostItStatus}
            onShare={handleSharePostIt}
            onRestore={handleRestorePostIt}
            onEmptyTrash={handleEmptyTrash}
            loadingStates={loadingStates}
            assignedFilter={assignedFilter}
            assignedEndDateFrom={assignedEndDateFrom}
            assignedEndDateTo={assignedEndDateTo}
            assignedQuarterlyPeriods={assignedQuarterlyPeriods}
            selectedIds={showQuickBar ? selectedIds : []}
            onToggleSelect={showQuickBar ? handleToggleSelect : undefined}
            onDataReady={setAssignedBoardIds}
            onEditAssigned={handleEditAssigned}
            onDeleteTemplate={handleDeleteAssignedTemplate}
          />
        </div>
      </div>

      <PostItModal
        variant="personal"
        isOpen={postItModalOpen}
        onClose={() => {
          setPostItModalOpen(false);
          setEditingPostIt(null);
        }}
        onSave={handleSavePostIt}
        postIt={editingPostIt || undefined}
        mode={personalPostItModalMode(editingPostIt)}
      />

      {postItToShare && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setPostItToShare(null);
          }}
          postIt={postItToShare}
          existingShares={cardExistingShares}
          onShareUsers={handleShareUsers}
          onShareRole={handleShareRole}
          onRemoveShare={handleRemoveShare}
        />
      )}

      <DeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setPostItToDelete(null);
          setIsHardDelete(false);
        }}
        onConfirm={confirmDeletePostIt}
        itemName={isHardDelete ? 'définitivement ce post-it' : 'ce post-it'}
        title={isHardDelete ? 'Supprimer définitivement' : 'Supprimer le post-it'}
        description={
          isHardDelete
            ? 'Cette action est irréversible. Le post-it sera supprimé définitivement.'
            : 'Le post-it sera déplacé dans la corbeille. Vous pourrez le restaurer ultérieurement.'
        }
        isLoading={
          isHardDelete ? hardDeletePostItMutation.isPending : archivePostItMutation.isPending
        }
      />
      <DeleteModal
        open={emptyTrashModalOpen}
        onClose={() => setEmptyTrashModalOpen(false)}
        onConfirm={confirmEmptyTrash}
        itemName="tous les post-its de la corbeille"
        title="Vider la corbeille"
        description="Cette action est irréversible. Tous vos post-its archivés seront supprimés définitivement."
        isLoading={emptyTrashMutation.isPending}
      />

      {canCreateAssigned && (
        <PostItModal
          variant="assigned"
          mode="create"
          isOpen={assignedModalOpen}
          onClose={() => setAssignedModalOpen(false)}
          onSave={handleSaveAssignedPostIt}
        />
      )}

      {editingAssignedPostIt && (
        <PostItModal
          variant="assigned"
          mode="edit"
          assignedPostIt={editingAssignedPostIt}
          isOpen={assignedViewModalOpen}
          onClose={() => {
            setAssignedViewModalOpen(false);
            setEditingAssignedPostIt(null);
          }}
          onSave={handleSaveAssignedPostIt}
          onUpdate={handleUpdateAssignedPostIt}
        />
      )}
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal
          open={syncOpen}
          onClose={handleCloseSync}
          lockedTypes={['postit']}
          filterSeed={postItCalendarFilterSeed(deadlineFilter, deadlineFrom, deadlineTo)}
        />
      ) : null}
    </>
  );
};
